/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@/hooks/use-navigate';

import type { ShopperSearch } from '@/scapi';

import { NativeSelect } from '@/components/ui/native-select';
import { PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';

/**
 * CategorySorting Component
 *
 * Renders a dropdown select control that allows users to sort product search results
 * by different criteria (e.g., brand, price, name, etc.). The component updates
 * the URL parameters when a new sorting option is selected, triggering a page navigation
 * to refresh the results with the new sort order.
 *
 * @param props - Component props
 * @param props.result - Product search result object from Salesforce Commerce Cloud
 * @param props.result.sortingOptions - Array of available sorting options with id and label
 * @param props.result.selectedSortingOption - Currently selected sorting option ID
 *
 * @returns A select dropdown for sorting options, or null if no sorting options are available
 *
 * @example
 * ```tsx
 * <CategorySorting result={productSearchResult} />
 * ```
 *
 * Features:
 * - Uses native select component with consistent UI styling
 * - Shows the label inline with the selection, so the closed control reads "Sort: Most Popular"
 * - Automatically updates URL parameters (sort, offset) when selection changes
 * - Accessible name supplied on the control itself, with no visible label element
 */
export default function CategorySorting({
    result,
}: {
    result: ShopperSearch.schemas['ProductSearchResult'];
}): ReactElement | null {
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const { t } = useTranslation('common');
    const isPending = navigation.state !== 'idle';

    const sortingOptions = useMemo(() => result?.sortingOptions || [], [result?.sortingOptions]);

    /**
     * Optimistic sorting option derived from the in-flight navigation target.
     *
     * While a navigation is pending, `navigation.location` holds the target location, allowing us to read the
     * intended sort param immediately. Once the navigation settles, we fall back to the server-provided value.
     */
    const reportedSortingOption = navigation.location
        ? new URLSearchParams(navigation.location.search).get(PRODUCT_SEARCH_QUERY_PARAMS.SORT) ||
          result.selectedSortingOption
        : result.selectedSortingOption;

    /**
     * What the control actually DISPLAYS, which on a first page load is not what the platform
     * reports.
     *
     * `lib/api/search.server.ts` deliberately omits the `sort` parameter when the URL carries none,
     * so the site's configured sorting rule owns the resting order. The response then comes back
     * with no `selectedSortingOption` at all — there is no selection to report, because the shopper
     * has not made one. A native select handed a value matching no option still has to show
     * something, and what it shows is the FIRST option.
     *
     * So the first option is the display fallback: it is the row the browser paints, and labelling
     * any other row would put the prefix somewhere the shopper cannot see it. This is also why the
     * control appeared unlabelled on a cold load while working after a sort — the reported value
     * was empty, so no row matched.
     *
     * The test is MEMBERSHIP, not presence (decision 2026-08-11, MR !44 review): a bookmarked
     * `?sort=<id>` naming a rule merchandising has since renamed reports a truthy id that matches no
     * row either, and a truthiness check would let it through to that same unlabelled control.
     *
     * The assumption this carries: the platform lists its default sorting rule first. True for the
     * SRAM configuration, where `sort.xml` assigns `best-matches` as the root and keyword-search
     * default and lists it first. If merchandising ever makes a non-first rule the default without
     * reordering the list, the control would name a sort the results are not in — the fix then is
     * for the response to report the resting selection, not for this component to guess harder.
     */
    const effectiveSortingOption = sortingOptions.some((option) => option.id === reportedSortingOption)
        ? reportedSortingOption
        : sortingOptions[0]?.id;

    /**
     * Announcement for a settled re-sort.
     *
     * A reordered list is otherwise a silent, visual-only event: the page does not navigate
     * anywhere a screen reader would announce, and the option the user picked is already in the
     * select, so nothing reports that the results themselves changed.
     *
     * Gated three ways so it only fires for a real re-sort:
     *  - only once a navigation has settled, so it describes the order actually on screen;
     *  - never on the first settle, because the initial ordering is not a change the user made;
     *  - only when the selected option differs from the last announced one, so filter and
     *    pagination navigations — which also settle through here — stay silent.
     *
     * Compared against the DISPLAYED option rather than the reported one, for the same reason the
     * prefix is: on a cold load the platform reports no selection, so a shopper who then picks the
     * option already showing would otherwise be told the list had been re-sorted when it had not.
     * Safe to read here despite it moving optimistically, because the guard above means this only
     * ever runs while idle, where it resolves to the settled value.
     */
    const announcedSortRef = useRef<string | null>(null);
    const [announcement, setAnnouncement] = useState('');

    useEffect(() => {
        if (navigation.state !== 'idle') {
            return;
        }
        const settledSort = effectiveSortingOption ?? '';
        if (announcedSortRef.current === null) {
            announcedSortRef.current = settledSort;
            return;
        }
        if (announcedSortRef.current === settledSort) {
            return;
        }
        announcedSortRef.current = settledSort;
        const label = sortingOptions.find((option) => option.id === settledSort)?.label ?? settledSort;
        setAnnouncement(label ? t('sorting.announcement', { option: label }) : '');
    }, [navigation.state, effectiveSortingOption, sortingOptions, t]);

    const navigatePage = useCallback(
        (sort: string) => {
            const params = new URLSearchParams(location.search);
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.SORT, sort);
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.OFFSET, '0');
            void navigate({
                ...location,
                search: `?${params.toString()}`,
            });
        },
        [location, navigate]
    );

    // Return null if no sorting options available
    if (sortingOptions.length === 0) {
        return null;
    }

    return (
        <div className={`inline-flex${isPending ? ' opacity-50 transition-opacity' : ''}`}>
            {/*
             * `aria-disabled` plus a guard, never the `disabled` attribute. Setting `disabled` on a
             * focused <select> moves focus to <body>, so a keyboard user who had just chosen an
             * option would lose their place for the length of the navigation — trading a minor
             * defect for a worse one. `aria-disabled` keeps the control focusable and reports it as
             * unavailable, and because it does not block interaction on its own, the `onChange`
             * guard below is what makes that claim true.
             */}
            {/*
             * No visible <label> element: the design paints the label INSIDE the closed control,
             * reading "Sort: Most Popular" (Figma PLP frame `1396:10895`, toolbar `0:31115`).
             *
             * A native select displays the selected <option>'s own text and nothing else, so the
             * only way to show a prefixed value while leaving the rest of the list bare is to
             * prefix that one option. Hence `selectedLabel` below: exactly one row carries the
             * prefix, and it is the row already marked as chosen. This supersedes the 2026-08-07
             * `optgroup`-heading treatment, which existed only because the closed control could
             * not carry the label at all; it now can, and a heading would paint "Sort" twice.
             *
             * The prefix is presentation, not a rename of the platform's option set: `option.id`
             * is untouched, the announcement below uses the bare label, and every unselected row
             * still reads exactly as the platform returned it.
             *
             * Keyed off `effectiveSortingOption`, not `result.selectedSortingOption`, so during a
             * pending navigation the prefix moves with the optimistic target rather than lagging a
             * step behind the value the control is already displaying.
             *
             * `aria-label` is NOT optional and NOT redundant with the visible prefix. Assistive
             * technology announces a select's VALUE as its content; without an explicit name this
             * would be an unnamed combobox — a WCAG 4.1.2 failure that trips axe's `select-name`
             * rule. It carries the same "Sort" string the prefix is built from, which also keeps
             * WCAG 2.5.3 (Label in Name) satisfied: the visible label text is contained in the
             * accessible name.
             */}
            <NativeSelect
                data-testid="category-sorting-select"
                aria-label={t('sorting.label')}
                aria-disabled={isPending || undefined}
                value={effectiveSortingOption || ''}
                onChange={(e) => {
                    if (isPending) {
                        return;
                    }
                    void navigatePage(e.target.value);
                }}>
                {sortingOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                        {option.id === effectiveSortingOption
                            ? t('sorting.selectedLabel', { option: option.label })
                            : option.label}
                    </option>
                ))}
            </NativeSelect>
            <span className="sr-only" aria-live="polite" aria-atomic={true}>
                {announcement}
            </span>
        </div>
    );
}
